export const templatesList = [
  "elevate",
  "eternal",
  "majestic",
  "legacy",
  "naruto",
  "orion",
  "ignite",
  "professional",
  "classic",
  "simple",
  "aurora",
  "maven",
  "vintage",
  "bronzor",
  "onyx",
  "rhyhorn",

  "horizon",
  "innovate",
  "nurture",
  "joyful",
  "intuify",
  "pixel",
  "essence",
  "snapshot",
  "automator",
  "medicareer",
  "aspire",
  "achiever",
  "palette",
  "designer",
  "genesis",
  "nexus",
  "zenith",
  "pinnacle",
  "sleek",
  "ethereal",
  "equinox",
  "azurill",
  "chikorita",
  "ditto",
  "gengar",
  "glalie",
  "kakuna",
  "leafish",
  "nosepass",
  "pikachu",
] as const;

export type Template = (typeof templatesList)[number];

// Curated set shown in the template picker. The full `templatesList` above is kept
// intact so the `Template` type, the config maps below, and any existing resume
// that references a now-hidden template all keep working — only the picker is
// trimmed. Ordered strongest-first; one best-in-class per visual archetype.
export const curatedTemplatesList: Template[] = [
  "classic", // single-col, serif, ATS
  "professional", // two-col, no photo, workhorse
  "aurora", // two-col + photo, neutral sidebar
  "sleek", // two-col + photo, colored header
  "orion", // single-col, minimal/ATS
  "naruto", // single-col, professional accent
  "elevate", // single-col, modern accent
  "ignite", // single-col, editorial
  "zenith", // two-col, neutral sidebar
  "ditto", // two-col + photo, cool accent
  "bronzor", // two-col + photo, warm accent
  "designer", // creative / design-forward
  "vintage", // single-col, refined serif
  "onyx", // photo-header single-col
  "pixel", // single-col, compact
  "pinnacle", // two-col + photo, serif
  "glalie", // two-col + photo, cool blue
  "intuify", // two-col + photo, modern chips
  "essence", // single-col, colored header band
  "nexus", // single-col, clean bold
  "eternal", // single-col, centered serif
  "majestic", // single-col, centered headers
  "legacy", // two-col, accent header
  "joyful", // creative, colorful + photo
  "nosepass", // formal europass-style CV
];

// Per-template identity presets: a font pairing + a premium accent color matched
// to each curated template's character. Applied on template switch so every
// template feels deliberately designed rather than a recolor of the same skin.
// Fonts are Google Fonts loaded on demand via webfontloader. The accent is used
// sparingly (name + a hairline), so it can carry a little richness.
export type TemplatePreset = { font: string; primary: string; variants: string[] };

const PRESET_VARIANTS = ["regular", "italic", "600", "700"];
const preset = (font: string, primary: string): TemplatePreset => ({
  font,
  primary,
  variants: PRESET_VARIANTS,
});

export const templatePresets: Partial<Record<Template, TemplatePreset>> = {
  classic: preset("Lora", "#1f2937"), // serif, ink — ATS-safe
  professional: preset("IBM Plex Sans", "#1e3a5f"), // navy executive
  aurora: preset("Lora", "#475569"), // neutral slate, soft serif
  sleek: preset("Inter", "#0f766e"), // modern teal
  orion: preset("Inter", "#334155"), // restrained slate, minimal
  naruto: preset("IBM Plex Sans", "#2f4b7c"), // professional blue
  elevate: preset("Work Sans", "#c2410c"), // burnt orange, modern
  ignite: preset("Newsreader", "#7c2d3a"), // editorial crimson serif
  zenith: preset("Manrope", "#475569"), // steel, neutral
  ditto: preset("IBM Plex Sans", "#0e7490"), // cool cyan
  bronzor: preset("Spectral", "#8a5a2b"), // warm bronze serif
  designer: preset("Space Grotesk", "#0d5c63"), // creative deep teal
  vintage: preset("PT Serif", "#6b4f3a"), // sepia, refined serif
  onyx: preset("IBM Plex Sans", "#27272a"), // graphite
  pixel: preset("Inter", "#18181b"), // compact ink
  pinnacle: preset("Spectral", "#1e3a5f"), // serif navy
  glalie: preset("Inter", "#1e5a8a"), // cool blue
  intuify: preset("Manrope", "#0e7490"), // modern cyan
  essence: preset("Work Sans", "#0f7a5a"), // emerald header band
  nexus: preset("Archivo", "#1f2937"), // clean bold ink
  eternal: preset("EB Garamond", "#44403c"), // centered serif, stone
  majestic: preset("Lora", "#6d2932"), // elegant burgundy
  legacy: preset("IBM Plex Serif", "#285943"), // forest serif
  joyful: preset("Figtree", "#be123c"), // friendly rose
  nosepass: preset("Lato", "#234e8c"), // formal CV blue
};

// Premium subset of the curated set (the rest are free).
export const premiumTemplateList = [
  "aurora",
  "sleek",
  "naruto",
  "elevate",
  "ignite",
  "zenith",
  "ditto",
  "bronzor",
  "designer",
  "vintage",
];

type layoutsListType = {
  [key in Template]: [[Array<string>, Array<string>]];
};

export const layoutsList: layoutsListType = {
  //jai
  orion: [
    [
      [
        "summary",
        "skills",
        "experience",
        "education",
        "projects",
        "volunteer",
        "references",
        "certifications",
        "awards",
        "interests",
        "publications",
        "languages",
        "profiles",
      ],
      [],
    ],
  ],
  legacy: [
    [
      [
        "summary",
        "skills",
        "education",
        "experience",
        "projects",
        "profiles",
        "volunteer",
        "references",
        "certifications",
        "awards",
        "publications",
        "interests",
        "languages",
      ],
      [],
    ],
  ],
  palette: [
    [
      [
        "experience",
        "education",
        "projects",
        "certifications",
        "awards",
        "volunteer",
        "references",
      ],
      ["summary", "skills", "profiles", "interests", "publications", "languages"],
    ],
  ],
  ignite: [
    [
      ["experience", "projects", "volunteer", "references"],
      [
        "education",
        "skills",
        "certifications",
        "profiles",
        "interests",
        "awards",
        "publications",
        "languages",
      ],
    ],
  ],
  joyful: [
    [
      ["experience", "education", "projects", "volunteer", "references"],
      [
        "summary",
        "skills",
        "interests",
        "certifications",
        "awards",
        "publications",
        "languages",
        "profiles",
      ],
    ],
  ],
  professional: [
    [
      ["summary", "experience", "projects", "volunteer", "references"],
      [
        "education",
        "skills",
        "certifications",
        "awards",
        "profiles",
        "publications",
        "interests",
        "languages",
      ],
    ],
  ],
  classic: [
    [
      [
        "summary",
        "skills",
        "experience",
        "education",
        "projects",
        "volunteer",
        "references",
        "profiles",
        "interests",
        "publications",
        "languages",
        "certifications",
        "awards",
      ],
      [],
    ],
  ],
  simple: [
    [
      ["summary", "experience", "projects", "skills", "volunteer", "references"],
      [
        "education",
        "certifications",
        "awards",
        "profiles",
        "interests",
        "publications",
        "languages",
      ],
    ],
  ],
  designer: [
    [
      ["summary", "experience", "education", "projects", "volunteer", "references"],
      ["profiles", "skills", "interests", "certifications", "awards", "publications", "languages"],
    ],
  ],
  //vivek
  majestic: [
    [
      [
        "summary",
        "experience",
        "education",
        "projects",
        "certifications",
        "awards",
        "skills",
        "volunteer",
        "references",
        "profiles",
        "interests",
        "publications",
        "languages",
      ],
      [],
    ],
  ],
  horizon: [
    [
      ["experience", "projects", "volunteer", "references"],
      [
        "profiles",
        "skills",
        "certifications",
        "education",
        "interests",
        "awards",
        "publications",
        "languages",
      ],
    ],
  ],
  elevate: [
    [
      [
        "summary",
        "experience",
        "education",
        "projects",
        "volunteer",
        "references",
        "profiles",
        "skills",
        "interests",
        "certifications",
        "awards",
        "publications",
        "languages",
      ],
      [],
    ],
  ],
  naruto: [
    [
      [
        "summary",
        "experience",
        "education",
        "projects",
        "volunteer",
        "references",
        "skills",
        "certifications",
        "awards",
        "profiles",
        "publications",
        "interests",
        "languages",
      ],
      [],
    ],
  ],
  nurture: [
    [
      ["summary", "experience", "education", "projects", "volunteer", "references"],
      ["profiles", "skills", "interests", "publications", "languages", "certifications", "awards"],
    ],
  ],
  eternal: [
    [
      [
        "summary",
        "skills",
        "projects",
        "experience",
        "education",
        "volunteer",
        "references",
        "profiles",
        "interests",
        "certifications",
        "awards",
        "publications",
        "languages",
      ],
      [],
    ],
  ],
  medicareer: [
    [
      [
        "summary",
        "experience",
        "education",
        "projects",
        "certifications",
        "awards",
        "skills",
        "volunteer",
        "references",
        "profiles",
        "publications",
        "interests",
        "languages",
      ],
      [],
    ],
  ],
  aspire: [
    [
      [
        "summary",
        "experience",
        "education",
        "projects",
        "certifications",
        "awards",
        "skills",
        "volunteer",
        "references",
        "profiles",
        "publications",
        "interests",
        "languages",
      ],
      [],
    ],
  ],
  achiever: [
    [
      ["experience", "projects", "volunteer", "references"],
      [
        "profiles",
        "skills",
        "certifications",
        "education",
        "interests",
        "awards",
        "publications",
        "languages",
      ],
    ],
  ],
  innovate: [
    [
      ["summary", "experience", "projects", "volunteer", "references"],
      [
        "profiles",
        "skills",
        "education",
        "certifications",
        "awards",
        "publications",
        "interests",
        "languages",
      ],
    ],
  ],
  pixel: [
    [
      [
        "summary",
        "education",
        "experience",
        "projects",
        "volunteer",
        "references",
        "skills",
        "certifications",
        "awards",
        "profiles",
        "publications",
        "interests",
        "languages",
      ],
      [],
    ],
  ],
  essence: [
    [
      [
        "summary",
        "experience",
        "education",
        "projects",
        "volunteer",
        "references",
        "skills",
        "certifications",
        "awards",
        "profiles",
        "publications",
        "interests",
        "languages",
      ],
      [],
    ],
  ],
  intuify: [
    [
      ["summary", "experience", "projects", "volunteer", "references"],
      [
        "skills",
        "certifications",
        "awards",
        "profiles",
        "education",
        "publications",
        "interests",
        "languages",
      ],
    ],
  ],
  //vamsi
  genesis: [
    [
      ["experience", "education", "certifications", "awards", "volunteer", "references"],
      ["summary", "skills", "projects", "profiles", "interests", "publications", "languages"],
    ],
  ],
  nexus: [
    [
      ["experience", "projects", "volunteer", "references"],
      [
        "profiles",
        "skills",
        "certifications",
        "education",
        "interests",
        "awards",
        "publications",
        "languages",
      ],
    ],
  ],
  zenith: [
    [
      ["summary", "experience", "projects", "volunteer", "references"],
      [
        "skills",
        "interests",
        "certifications",
        "awards",
        "publications",
        "languages",
        "profiles",
        "education",
      ],
    ],
  ],
  pinnacle: [
    [
      ["summary", "experience", "education", "certifications", "awards", "volunteer", "references"],
      ["skills", "profiles", "projects", "publications", "interests", "languages"],
    ],
  ],
  aurora: [
    [
      ["summary", "experience", "education", "projects", "volunteer", "references"],
      ["certifications", "awards", "skills", "profiles", "interests", "publications", "languages"],
    ],
  ],
  maven: [
    [
      [
        "summary",
        "education",
        "interests",
        "experience",
        "projects",
        "skills",
        "volunteer",
        "references",
        "profiles",
        "certifications",
        "awards",
        "publications",
        "languages",
      ],
      [],
    ],
  ],
  vintage: [
    [
      [
        "summary",
        "experience",
        "education",
        "projects",
        "skills",
        "volunteer",
        "references",
        "profiles",
        "interests",
        "certifications",
        "awards",
        "publications",
        "languages",
      ],
      [],
    ],
  ],
  sleek: [
    [
      ["summary", "experience", "education", "volunteer", "references"],
      [
        "profiles",
        "skills",
        "interests",
        "certifications",
        "projects",
        "awards",
        "publications",
        "languages",
      ],
    ],
  ],
  ethereal: [
    [
      ["summary", "experience", "projects", "awards", "volunteer", "references"],
      [
        "profiles",
        "education",
        "skills",
        "interests",
        "certifications",
        "publications",
        "languages",
      ],
    ],
  ],
  equinox: [
    [
      ["summary", "experience", "projects", "awards", "volunteer", "references"],
      [
        "profiles",
        "education",
        "skills",
        "interests",
        "certifications",
        "publications",
        "languages",
      ],
    ],
  ],
  //previous
  azurill: [
    [
      ["summary", "experience", "education", "awards", "volunteer", "references"],
      [
        "profiles",
        "skills",
        "interests",
        "certifications",
        "projects",
        "publications",
        "languages",
      ],
    ],
  ],
  bronzor: [
    [
      [
        "profiles",
        "summary",
        "experience",
        "education",
        "projects",
        "skills",
        "volunteer",
        "references",
        "certifications",
        "interests",
        "awards",
        "publications",
        "languages",
      ],
      [],
    ],
  ],
  chikorita: [
    [
      ["summary", "experience", "education", "projects", "volunteer", "references"],
      ["skills", "interests", "certifications", "awards", "publications", "languages", "profiles"],
    ],
  ],
  // developerx: [
  //   [
  //     [
  //       "summary",
  //       "experience",
  //       "education",
  //       "projects",
  //       "certifications",
  //       "awards",
  //       "volunteer",
  //       "references",
  //     ],
  //     ["skills", "profiles", "publications", "interests", "languages"],
  //   ],
  // ],
  ditto: [
    [
      ["summary", "experience", "education", "projects", "volunteer", "references"],
      ["profiles", "skills", "interests", "publications", "languages", "certifications", "awards"],
    ],
  ],
  gengar: [
    [
      ["summary", "experience", "education", "projects", "volunteer", "references"],
      ["profiles", "skills", "interests", "certifications", "awards", "publications", "languages"],
    ],
  ],
  glalie: [
    [
      ["summary", "experience", "education", "projects", "volunteer", "references"],
      ["profiles", "skills", "interests", "certifications", "awards", "publications", "languages"],
    ],
  ],
  kakuna: [
    [
      [
        "summary",
        "experience",
        "education",
        "projects",
        "certifications",
        "awards",
        "skills",
        "volunteer",
        "references",
        "profiles",
        "interests",
        "publications",
        "languages",
      ],
      [],
    ],
  ],
  snapshot: [
    [
      [
        "summary",
        "education",
        "experience",
        "projects",
        "skills",
        "volunteer",
        "references",
        "certifications",
        "awards",
        "profiles",
        "publications",
        "interests",
        "languages",
      ],
      [],
    ],
  ],
  automator: [
    [
      [
        "summary",
        "education",
        "experience",
        "projects",
        "skills",
        "volunteer",
        "references",
        "certifications",
        "awards",
        "profiles",
        "publications",
        "interests",
        "languages",
      ],
      [],
    ],
  ],
  leafish: [
    [
      ["experience", "projects", "volunteer", "references"],
      [
        "profiles",
        "skills",
        "certifications",
        "education",
        "interests",
        "awards",
        "publications",
        "languages",
      ],
    ],
  ],
  nosepass: [
    [
      [
        "summary",
        "experience",
        "education",
        "projects",
        "skills",
        "volunteer",
        "references",
        "interests",
        "certifications",
        "awards",
        "publications",
        "languages",
        "profiles",
      ],
      [],
    ],
  ],
  onyx: [
    [
      [
        "summary",
        "experience",
        "education",
        "projects",
        "certifications",
        "awards",
        "skills",
        "volunteer",
        "references",
        "profiles",
        "publications",
        "interests",
        "languages",
      ],
      [],
    ],
  ],
  pikachu: [
    [
      ["summary", "experience", "education", "projects", "volunteer", "references"],
      ["profiles", "skills", "interests", "publications", "languages", "certifications", "awards"],
    ],
  ],
  rhyhorn: [
    [
      [
        "profiles",
        "summary",
        "experience",
        "education",
        "projects",
        "skills",
        "volunteer",
        "references",
        "certifications",
        "interests",
        "awards",
        "publications",
        "languages",
      ],
      [],
    ],
  ],
};

type colorList = {
  [key in Template]: string;
};

export const colorList = {
  //jai
  orion: "light-grey",
  legacy: "black",
  palette: "black",
  joyful: "black",
  ignite: "black",
  professional: "black",
  classic: "black",
  simple: "black",
  designer: "black",
  //vivek
  majestic: "black",
  eternal: "grey",
  naruto: "#395796",
  nurture: "#302b8f",
  elevate: "#ea580c",
  innovate: "#b8afa3",
  horizon: "#372f25",
  pixel: "#000000",
  essence: "#098960",
  intuify: "#1ca7c9",
  medicareer: "#304572",
  aspire: "#000000",
  achiever: "#000000",
  snapshot: "#000000",
  automator: "#000000",
  //vamsi
  genesis: "#0284c7",
  nexus: "black",
  zenith: "#b0baca",
  pinnacle: "black",
  aurora: "#57534e",
  maven: "black",
  vintage: "#57534e",
  sleek: "black",
  ethereal: "#dddddd",
  equinox: "#ffffff",
  //old
  azurill: "#ca8a04",
  bronzor: "black",
  chikorita: "#059669",
  // developerx: "black",
  ditto: "#0891b2",
  gengar: "#0891b2",
  glalie: "#0891b2",
  kakuna: "black",
  leafish: "#563737",
  nosepass: "black",
  onyx: "black",
  pikachu: "#ca8a04",
  rhyhorn: "black",
};

type marginListType = {
  [key in Template]: number;
};

export const marginList: marginListType = {
  //jai
  orion: 40,
  legacy: 14,
  palette: 14,
  joyful: 36,
  ignite: 14,
  professional: 14,
  classic: 48,
  simple: 32,
  designer: 24,
  //vivek
  majestic: 40,
  eternal: 14,
  naruto: 30,
  nurture: 14,
  elevate: 40,
  innovate: 14,
  horizon: 14,
  pixel: 40,
  essence: 14,
  intuify: 40,
  medicareer: 14,
  aspire: 14,
  achiever: 14,
  snapshot: 14,
  automator: 14,
  //vamsi
  genesis: 14,
  nexus: 32,
  zenith: 36,
  pinnacle: 36,
  aurora: 36,
  maven: 34,
  vintage: 34,
  sleek: 34,
  ethereal: 14,
  equinox: 14,
  //old
  azurill: 14,
  bronzor: 14,
  chikorita: 14,
  // developerx: 14,
  ditto: 40,
  gengar: 14,
  glalie: 40,
  kakuna: 14,
  leafish: 14,
  nosepass: 14,
  onyx: 40,
  pikachu: 14,
  rhyhorn: 14,
};

type singleColumnListType = {
  [key in Template]: boolean;
};

export const singleColumnList: singleColumnListType = {
  // set to true if single column
  orion: true,
  legacy: true,
  classic: true,
  majestic: true,
  eternal: true,
  naruto: true,
  elevate: true,
  pixel: true,
  essence: true,
  medicareer: true,
  aspire: true,
  snapshot: true,
  automator: true,
  maven: true,
  vintage: true,
  bronzor: true,
  kakuna: true,
  nosepass: true,
  rhyhorn: true,
  onyx: true,

  palette: false,
  joyful: false,
  ignite: false,
  professional: false,
  simple: false,
  designer: false,
  nurture: false,
  innovate: false,
  horizon: false,
  intuify: false,
  achiever: false,
  genesis: false,
  nexus: false,
  zenith: false,
  pinnacle: false,
  aurora: false,
  sleek: false,
  ethereal: false,
  equinox: false,
  azurill: false,
  chikorita: false,
  ditto: false,
  gengar: false,
  glalie: false,
  leafish: false,
  pikachu: false,
  // developerx: "black",
};
